import React, { useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ApartmentOutlined, EditOutlined, PlusOutlined, SettingOutlined, SyncOutlined } from '@ant-design/icons';
import LoginStatusIndicator from '@components/common/elements/LoginStatusIndicator';
import PickUpDisplaySettingModal from '@components/common/form/PickUpDisplaySettingModal';
import ListPage from '@components/common/layout/ListPage';
import { DATE_FORMAT_VALUE, DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import { orderDisplayLayouts } from '@constants/masterData';
import useFetch from '@hooks/useFetch';
import useListBase from '@hooks/useListBase';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import { convertUtcToLocalTime, orderNumber } from '@utils';
import { Button, Table } from 'antd';

import useDeviceOrderChildFormModal from './DeviceOrderChildFormModal';

import styles from './index.module.scss';

const messageIntl = defineMessages({
    objectName: 'Device Kitplay',
    titleModalDeleteMapping: 'Are you sure you want to delete this mapping?',
    deleteSuccessfully: 'Delete successfully',
    deleteFailed: 'Delete failed',
});
function Device({ deviceId }) {
    const translate = useTranslate();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const searchParams = new URLSearchParams(location.search);
    const notification = useNotification();

    const [childData, setChildData] = useState({});
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [loadingSubItems, setLoadingSubItems] = useState({});
    const [fetchedAt, setFetchedAt] = useState(null);

    const [openSettingModal, setOpenSettingModal] = useState(false);
    const [selectedSettingRecord, setSelectedSettingRecord] = useState(null);

    const [editingRowId, setEditingRowId] = useState(null);
    const [editingSettingId, setEditingSettingId] = useState(null);

    const handleOpenSetting = (record) => {
        setSelectedSettingRecord(record);
        setOpenSettingModal(true);
        setEditingSettingId(record.id);
    };

    const handleCloseSettingModal = () => {
        setOpenSettingModal(false);
        setSelectedSettingRecord(null);
        setEditingSettingId(null);
    };

    const handleCreateClick = () => {
        openChildCreate();
    };

    const { data, mixinFuncs, queryFilter, loading, pagination, changePagination, queryParams, serializeParams } =
        useListBase({
            apiConfig: apiConfig.orderDisplay,
            options: {
                pageSize: DEFAULT_TABLE_ITEM_SIZE,
                objectName: translate.formatMessage(messageIntl.objectName),
            },
            params: {
                parentId: deviceId,
            },
            override: (funcs) => {
                funcs.mappingData = (response) => {
                    if (response.result === true) {
                        setFetchedAt(new Date());
                        return {
                            data: response.data.content,
                            total: response.data.totalElements,
                        };
                    }
                };
                funcs.getList = () => {
                    mixinFuncs.handleFetchList({
                        parentId: deviceId,
                    });
                };
                funcs.getItemDetailLink = (dataRow) => {
                    const currentPath = location.pathname.replace('group-device', 'device-child');
                    const searchParams = location.search;
                    return `${currentPath}/${dataRow.id}${searchParams}`;
                };

                funcs.getCreateLink = () => {
                    const currentPath = location.pathname.replace('group-device', 'device-child');
                    const searchParams = location.search;
                    return `${currentPath}/create${searchParams}`;
                };
                funcs.additionalActionColumnButtons = () => {
                    return {
                        editChild: (record) => {
                            if (!mixinFuncs.hasPermission([apiConfig.orderDisplay.update.baseURL])) return null;
                            return (
                                <BaseTooltip title={translate.formatMessage(commonMessage.edit)}>
                                    <Button
                                        type="link"
                                        style={{ padding: 0 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingRowId(record.id);
                                            openChildEdit(record);
                                        }}
                                    >
                                        <EditOutlined />
                                    </Button>
                                </BaseTooltip>
                            );
                        },
                        loginStatus: (record) => {
                            return <LoginStatusIndicator lastTimeLogin={record.device.lastTimeLogin} fetchedAt={fetchedAt} translate={translate} />;
                        },
                        setting: (record) => {
                            return (
                                <BaseTooltip title={translate.formatMessage(commonMessage.editSetting)}>
                                    <Button
                                        type="link"
                                        style={{ padding: 0 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenSetting(record);
                                        }}
                                    >
                                        <SettingOutlined />
                                    </Button>
                                </BaseTooltip>
                            );
                        },
                        mapping: (record) => {
                            return (
                                <BaseTooltip title={translate.formatMessage(commonMessage.mappingOrderDisplay)}>
                                    <Button
                                        type="link"
                                        style={{ padding: 0 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const deviceName = searchParams.get('deviceName') || '';
                                            navigate(
                                                `${generatePath(routes.orderDisplayMappingListPage.path, {
                                                    customerId: params.customerId,
                                                })}?deviceId=${deviceId}&deviceName=${encodeURIComponent(
                                                    deviceName,
                                                )}&orderDisplayId=${record.id}&orderDisplayName=${encodeURIComponent(
                                                    record?.device?.name || '',
                                                )}`,
                                            );
                                        }}
                                    >
                                        <ApartmentOutlined />
                                    </Button>
                                </BaseTooltip>
                            );
                        },
                    };
                };
            },
        });

    const { openChildCreate, openChildEdit, renderModals } = useDeviceOrderChildFormModal({
        deviceId: deviceId,
        onReload: () => {
            mixinFuncs.getList(),
            setEditingRowId(null);
        },
        onClose: () => setEditingRowId(null),
    });

    const columns = [
        {
            title: '#',
            dataIndex: 'index',
            key: 'id',
            render: (text, record, index) => {
                return orderNumber(pagination, index);
            },
            width: 50,
        },
        {
            title: <FormattedMessage defaultMessage="Device ID" />,
            dataIndex: ['device', 'deviceId'],
            width: 120,
        },
        {
            title: <FormattedMessage defaultMessage="Device name" />,
            dataIndex: ['device', 'name'],
            key: 'name',
        },
        {
            title: <FormattedMessage defaultMessage="Expiration date" />,
            width: 180,
            dataIndex: ['device', 'expireDate'],
            align: 'right',
            render: (expireDate) => {
                const expireDateLocal = convertUtcToLocalTime(expireDate, DATE_FORMAT_VALUE, DATE_FORMAT_VALUE);
                return <div>{expireDateLocal}</div>;
            },
        },
        {
            title: <FormattedMessage defaultMessage="Layout" />,
            width: 120,
            dataIndex: 'layout',
            render: (layout) => {
                const option = orderDisplayLayouts.find((item) => item.value === layout);
                return option ? translate.formatMessage(option.label) : null;
            },
        },
        mixinFuncs.renderStatusColumn({
            width: '120px',
            dataIndex: ['device', 'status'],
        }),
        mixinFuncs.renderActionColumn(
            {
                mapping: mixinFuncs.hasPermission([apiConfig.orderDisplayMapping.getList.baseURL]),
                setting: { ignorePermission: true },
                editChild: mixinFuncs.hasPermission([apiConfig.orderDisplay.update.baseURL]),
                delete: mixinFuncs.hasPermission([apiConfig.orderDisplay.delete.baseURL]),
                loginStatus: true,
            },
            { width: '140px', className: styles['action-column'] },
        ),
    ].filter(Boolean);

    const { execute: fetchSubItems } = useFetch(apiConfig.orderDisplayMapping.getList, {
        immediate: false,
        mappingData: (response) => {
            if (response.result === true) {
                return {
                    data: response.data.content,
                    total: response.data.totalElements,
                };
            } else {
                console.error('Error: Invalid response format', response);
            }
        },
    });

    const handleFetch = (record) => {
        const params = { orderDisplayId: record.id };
        setLoadingSubItems((prev) => ({
            ...prev,
            [record.id]: true,
        }));
        fetchSubItems({
            params,
            onCompleted: (response) => {
                if (response && response.data) {
                    const filteredData = response.data.content || [];
                    setChildData((prevData) => ({
                        ...prevData,
                        [record.id]: filteredData,
                    }));
                } else {
                    notification({
                        message: 'Error',
                        type: 'error',
                    });
                }
                setLoadingSubItems((prev) => ({
                    ...prev,
                    [record.id]: false,
                }));
            },
            onError: () => {
                notification({
                    message: 'Error',
                    type: 'error',
                });
                setLoadingSubItems((prev) => ({
                    ...prev,
                    [record.id]: false,
                }));
            },
        });
    };

    const handleExpand = (expanded, record) => {
        if (expanded) {
            if (!childData[record.id]) {
                handleFetch(record);
            }
            setExpandedRowKeys((prevKeys) => [...prevKeys, record.id]);
        } else {
            setExpandedRowKeys((prevKeys) => prevKeys.filter((id) => id !== record.id));
        }
    };

    const expandedRowRender = (record) => {
        const childItems = childData[record.id] || [];
        const isLoading = loadingSubItems[record.id] || false;
        const childColumns = [
            {
                dataIndex: 'orderDisplayNumber',
                render: (data) => {
                    return (
                        <>
                            {translate.formatMessage(commonMessage.readyBoardMappingNumber)} {data}
                        </>
                    );
                },
            },
        ];
        return (
            <>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 1,
                        marginBottom: -18,
                    }}
                >
                    <span style={{ fontWeight: 'bold' }}>
                        {translate.formatMessage(commonMessage.readyBoardMapping)}
                    </span>
                    <SyncOutlined style={{ color: '#1677ff', cursor: 'pointer' }} onClick={() => handleFetch(record)} />
                </div>
                <Table
                    columns={childColumns}
                    dataSource={childItems}
                    pagination={false}
                    rowKey={(item) => item.id}
                    loading={isLoading}
                />
            </>
        );
    };

    return (
        <>
            <ListPage
                baseTable={
                    <>
                        <div className={styles.btn}>
                            <Button
                                type="primary"
                                style={{ marginBottom: 16 }}
                                onClick={handleCreateClick}
                                icon={<PlusOutlined />}
                            >
                            </Button>
                        </div>
                        <BaseTable
                            onChange={changePagination}
                            pagination={pagination}
                            loading={loading}
                            dataSource={data}
                            columns={columns}
                            expandable={{
                                expandedRowRender,
                                expandedRowKeys: expandedRowKeys,
                                defaultExpandedRowKeys: ['0'],
                                onExpand: handleExpand,
                            }}
                            rowKey="id"
                            onRow={(record) => ({
                                onClick: (e) => {
                                    e.stopPropagation();
                                },
                                className: record.id === editingRowId || record.id === editingSettingId ? styles['highlighted-row'] : '',
                            })}
                        />
                    </>
                }
            />
            {renderModals()}
            <PickUpDisplaySettingModal
                open={openSettingModal}
                onClose={handleCloseSettingModal}
                selectedRecord={selectedSettingRecord}
                restaurantId={params.customerId} 
                onSaveSuccess={() => mixinFuncs.getList()} 
            />
        </>
    );
}
export default Device;
